import React, { useState, useEffect } from "react";
import { List as ListEntity } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useData } from "../components/DataContext";
import AuthLoader from "../components/auth/AuthLoader";
import SignInRequired from "../components/auth/SignInRequired";
import type { List } from "@/types";

export default function CreateList() {
  const navigate = useNavigate();
  const { folders, lists, sections, listItems, runOptimisticUpdate, setOptimisticData, user, userLoading } = useData();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const defaultFolderId = urlParams.get('folder_id');
    if (defaultFolderId) {
      setFolderId(defaultFolderId);
    }
  }, []);

  const handleCreate = async () => {
    if (!title.trim()) return;

    setIsCreating(true);

    const originalState = { lists: [...lists] };
    const tempListId = `temp-${Date.now()}`;
    const position = lists.filter(l => l.folder_id === folderId).length;

    const newListData = {
      title: title.trim(),
      ...(folderId && { folder_id: folderId }),
      position: position
    };

    const task = async () => {
      try {
        const newList = await ListEntity.create(newListData);
        setOptimisticData(prevData => ({
          ...prevData,
          lists: prevData.lists.map(l => 
            l.id === tempListId ? { ...l, ...newList, id: newList.id } : l
          )
        }));
        navigate(createPageUrl(`EditList?id=${newList.id}`));
      } catch (error) {
        console.error("Error creating list:", error);
        setIsCreating(false);
      }
    };

    const newState = {
      folders: [...folders],
      lists: [...lists, { id: tempListId, ...newListData, inherited_list_ids: [] } as List],
      sections: [...sections],
      listItems: [...listItems]
    };

    runOptimisticUpdate(task, { update: newState, originalState: { folders, ...originalState, sections, listItems } });
  };

  if (userLoading) {
    return <AuthLoader />;
  }

  if (!user) {
    return <SignInRequired />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(createPageUrl("Dashboard"))}
            className="border-slate-200 hover:bg-slate-50"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Create New List</h1>
            <p className="text-slate-600 mt-1">Start organizing with a new list</p>
          </div>
        </div>

        {/* Form */}
        <Card className="shadow-lg border-0 bg-white">
          <CardHeader className="pb-6">
            <CardTitle className="text-xl text-slate-900">List Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium text-slate-700">
                List Title *
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter list title..."
                className="h-12 border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="folder" className="text-sm font-medium text-slate-700">
                Folder (Optional)
              </Label>
              <Select value={folderId === null ? "none" : folderId} onValueChange={(value) => setFolderId(value === "none" ? null : value)}>
                <SelectTrigger className="h-12 border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <SelectValue placeholder="Choose a folder..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No folder</SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-6">
              <Button
                variant="outline"
                onClick={() => navigate(createPageUrl("Dashboard"))}
                className="border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!title.trim() || isCreating}
                className="bg-blue-600 hover:bg-blue-700 shadow-lg"
              >
                <Save className="w-4 h-4 mr-2" />
                {isCreating ? 'Creating...' : 'Create List'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
